
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Code, 
  Play, 
  BookOpen, 
  Terminal,
  ChevronDown,
  RefreshCw,
  Save,
  CheckCircle,
  AlertTriangle,
  Clock,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CodeEditor from "./CodeEditor";
import { DatasetSelector } from "@/components/DatasetSelector";
import TestResultsPanel from "@/components/TestResultsPanel";
import { isPyodideLoaded } from "@/utils/pyodideLoader";
import { 
  getCodeTemplate, 
  executeCode, 
  saveCodeToLocalStorage,
  loadCodeFromLocalStorage,
  clearSavedCode,
  isChallengeCompleted,
  getCompletedChallenges,
  type TestResult,
} from "@/utils/codeExecutionUtils";
import { trackPlaygroundInteraction } from "@/utils/analyticsTracker";
import { useAuth } from "@/context/AuthContext";

interface PlaygroundExample {
  id: string;
  title: string;
  description: string;
  language: "python" | "javascript" | "tensorflow";
  imageUrl?: string;
  difficulty: "Beginner" | "Advanced";
  tags: string[];
  companies?: string;
  isActive?: boolean;
}

const playgroundExamples: PlaygroundExample[] = [
  // Beginner Examples
  {
    id: "sigmoid-function",
    title: "Implement the Sigmoid Function",
    description: "Write a Python function to compute the sigmoid of a given input.",
    language: "python",
    difficulty: "Beginner",
    tags: ["Probability", "Activation Functions"],
    companies: "Snapchat",
    isActive: true
  },
  {
    id: "cross-entropy-loss",
    title: "Compute Cross-Entropy Loss",
    description: "Implement a function to calculate the cross-entropy loss between predicted probabilities and actual labels.",
    language: "python",
    difficulty: "Beginner",
    tags: ["Loss Functions", "Probability"],
    companies: "Netflix",
    isActive: true
  },
  {
    id: "decision-stump",
    title: "Implement a Decision Stump",
    description: "Create a simple decision stump (a one-level decision tree) classifier.",
    language: "python",
    difficulty: "Beginner",
    tags: ["Decision Trees", "Classification"],
    companies: "Pinterest",
    isActive: true
  },
  {
    id: "mean-variance",
    title: "Calculate Mean and Variance",
    description: "Write functions to compute the mean and variance of a dataset.",
    language: "python",
    difficulty: "Beginner",
    tags: ["Statistics", "Probability"],
    companies: "",
    isActive: true
  },
  {
    id: "knn-classifier",
    title: "Implement K-Nearest Neighbors (KNN)",
    description: "Build a simple KNN classifier from scratch.",
    language: "python",
    difficulty: "Beginner",
    tags: ["Classification", "Distance Metrics"],
    companies: "Uber",
    isActive: true
  },
  {
    id: "gradient-mse",
    title: "Compute the Gradient of Mean Squared Error (MSE)",
    description: "Derive and implement the gradient of the MSE loss function.",
    language: "python",
    difficulty: "Beginner",
    tags: ["Optimization", "Regression"],
    companies: "Netflix",
    isActive: true
  },
  {
    id: "feature-scaling",
    title: "Implement Feature Scaling (Normalization)",
    description: "Write a function to normalize features in a dataset.",
    language: "python",
    difficulty: "Beginner",
    tags: ["Data Preprocessing"],
    companies: "",
    isActive: false
  },
  {
    id: "entropy-calculation",
    title: "Calculate Entropy of a Dataset",
    description: "Compute the entropy of a dataset for a classification problem.",
    language: "python",
    difficulty: "Beginner",
    tags: ["Information Theory", "Decision Trees"],
    companies: "",
    isActive: false
  },
  
  // Advanced Examples
  {
    id: "backpropagation",
    title: "Implement Backpropagation for a Neural Network",
    description: "Build a simple neural network and implement the backpropagation algorithm for training.",
    language: "python",
    difficulty: "Advanced",
    tags: ["Neural Networks", "Optimization"],
    companies: "Common in various ML interviews",
    isActive: true
  },
  {
    id: "decision-tree",
    title: "Build a Decision Tree Classifier",
    description: "Implement a decision tree classifier using information gain as the splitting criterion.",
    language: "python",
    difficulty: "Advanced",
    tags: ["Decision Trees", "Classification"],
    companies: "Common in various ML interviews",
    isActive: true
  },
  {
    id: "pca",
    title: "Implement Principal Component Analysis (PCA)",
    description: "Perform PCA on a dataset to reduce its dimensionality.",
    language: "python",
    difficulty: "Advanced",
    tags: ["Dimensionality Reduction", "Linear Algebra"],
    companies: "Common in various ML interviews",
    isActive: true
  },
  {
    id: "svm",
    title: "Implement Support Vector Machine (SVM) with a Linear Kernel",
    description: "Build an SVM classifier using a linear kernel from scratch.",
    language: "python",
    difficulty: "Advanced",
    tags: ["Classification", "Optimization"],
    companies: "Common in various ML interviews",
    isActive: true
  },
  {
    id: "em-algorithm",
    title: "Implement the Expectation-Maximization (EM) Algorithm",
    description: "Apply the EM algorithm to fit a Gaussian Mixture Model to data.",
    language: "python",
    difficulty: "Advanced",
    tags: ["Clustering", "Probability"],
    companies: "Common in various ML interviews",
    isActive: true
  },
  {
    id: "gradient-boosting",
    title: "Implement Gradient Boosting Algorithm",
    description: "Build a gradient boosting model for regression tasks.",
    language: "python",
    difficulty: "Advanced",
    tags: ["Ensemble Methods", "Regression"],
    companies: "Common in various ML interviews",
    isActive: true
  },
  {
    id: "convolution",
    title: "Implement Convolution Operation for Images",
    description: "Perform a 2D convolution operation on an image with a given kernel.",
    language: "python",
    difficulty: "Advanced",
    tags: ["Computer Vision", "Convolutional Neural Networks"],
    companies: "Common in various ML interviews",
    isActive: true
  },
  {
    id: "k-means",
    title: "Implement K-Means Clustering",
    description: "Build a K-means clustering algorithm from scratch to group data points.",
    language: "python",
    difficulty: "Advanced",
    tags: ["Clustering", "Unsupervised Learning"],
    companies: "Common in various ML interviews",
    isActive: true
  },
  {
    id: "logistic-regression",
    title: "Implement Logistic Regression Using Gradient Descent",
    description: "Build a logistic regression model trained using gradient descent.",
    language: "python",
    difficulty: "Advanced",
    tags: ["Classification", "Optimization"],
    companies: "Common in various ML interviews",
    isActive: true
  },
  {
    id: "naive-bayes",
    title: "Implement Naive Bayes Classifier",
    description: "Create a simple Naive Bayes classifier for text classification.",
    language: "python",
    difficulty: "Advanced",
    tags: ["Probability", "Text Classification"],
    companies: "Common in various ML interviews",
    isActive: true
  }
];

const CodingPlayground = () => {
  const { user } = useAuth();
  const [selectedSection, setSelectedSection] = useState<"theory" | "practice">("theory");
  const [selectedCategory, setSelectedCategory] = useState<"python" | "javascript" | "tensorflow">("python");
  const [selectedChallenge, setSelectedChallenge] = useState<string | null>(null);
  const [code, setCode] = useState<string>("");
  const [output, setOutput] = useState<string>("$ Run your code to see the output");
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isPyodideReady, setIsPyodideReady] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [completedChallenges, setCompletedChallenges] = useState<string[]>([]);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState<boolean>(false);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [testStats, setTestStats] = useState({ passed: 0, total: 0 });
  
  // Filter playground examples based on selected section and language
  const filteredExamples = playgroundExamples
    .filter(example => example.isActive !== false)
    .filter(example => example.language === selectedCategory)
    .filter(example => {
      if (selectedSection === "theory") {
        return example.difficulty === "Beginner";
      } else {
        return example.difficulty === "Advanced";
      }
    });

  // Load completed challenges and check Pyodide status
  useEffect(() => {
    setCompletedChallenges(getCompletedChallenges());
    setIsPyodideReady(isPyodideLoaded());
  }, []);

  // Load code template when challenge changes
  useEffect(() => {
    if (selectedChallenge) {
      const template = getCodeTemplate(selectedChallenge);
      const savedCode = loadCodeFromLocalStorage(selectedChallenge, template);
      setCode(savedCode);
      setOutput("$ Run your code to see the output");
      setHasError(false);
    }
  }, [selectedChallenge]);

  // Run code handler
  const handleRunCode = async () => {
    setIsRunning(true);
    setOutput("Running tests...");
    setTestResults([]);
    setHasError(false);

    try {
      if (selectedChallenge) {
        const challengeData = playgroundExamples.find(ex => ex.id === selectedChallenge);
        
        if (!isPyodideReady) {
          toast.info("Loading Python environment... This may take ~10 seconds.");
        }

        const result = await executeCode(code, selectedChallenge);

        if (!isPyodideReady) {
          setIsPyodideReady(true);
        }

        setOutput(result.output);
        setHasError(!result.success);

        if (result.testResults) {
          setTestResults(result.testResults);
          setTestStats({
            passed: result.passedTests || 0,
            total: result.totalTests || 0,
          });
        }

        // Track code execution
        trackPlaygroundInteraction("code_execution", {
          userId: user?.id,
          userEmail: user?.email,
          extraData: {
            challengeName: challengeData?.title,
            challengeId: selectedChallenge,
            difficulty: challengeData?.difficulty,
            language: challengeData?.language,
            success: result.success,
            hasError: !result.success,
            testsPassed: result.passedTests || 0,
            testsTotal: result.totalTests || 0,
            codeLength: code.length,
          },
        });

        if (result.success) {
          setShowSuccessAnimation(true);
          setTimeout(() => setShowSuccessAnimation(false), 2000);

          // Update completed challenges list
          const updated = getCompletedChallenges();
          setCompletedChallenges(updated);

          // Track challenge completion
          trackPlaygroundInteraction("challenge_completed", {
            userId: user?.id,
            userEmail: user?.email,
            extraData: {
              challengeName: challengeData?.title,
              challengeId: selectedChallenge,
              difficulty: challengeData?.difficulty,
              language: challengeData?.language,
              tags: challengeData?.tags,
            },
          });

          toast.success("All tests passed! 🎉", {
            description: "Great job! Challenge completed."
          });
        } else {
          toast.error(result.error || "Some tests failed", {
            description: "Check the test results below for details."
          });
        }
      }
    } catch (error) {
      setOutput(`Error: ${error instanceof Error ? error.message : String(error)}`);
      setHasError(true);
      toast.error("Failed to execute code");
    } finally {
      setIsRunning(false);
    }
  };
  
  // Save code handler
  const handleSaveCode = () => {
    if (selectedChallenge) {
      const challengeData = playgroundExamples.find(ex => ex.id === selectedChallenge);
      saveCodeToLocalStorage(selectedChallenge, code);
      
      trackPlaygroundInteraction("code_saved", {
        userId: user?.id,
        userEmail: user?.email,
        extraData: {
          challengeName: challengeData?.title,
          challengeId: selectedChallenge,
          codeLength: code.length,
        },
      });
      
      toast.success("Code saved successfully!");
    }
  };
  
  // Reset code handler
  const handleResetCode = () => {
    if (selectedChallenge) {
      if (window.confirm("Are you sure you want to reset your code? All changes will be lost.")) {
        const challengeData = playgroundExamples.find(ex => ex.id === selectedChallenge);
        const template = getCodeTemplate(selectedChallenge);
        setCode(template);
        setTestResults([]);
        setTestStats({ passed: 0, total: 0 });
        clearSavedCode(selectedChallenge);
        setOutput("$ Code has been reset to the original template");
        setHasError(false);
        
        trackPlaygroundInteraction("code_reset", {
          userId: user?.id,
          userEmail: user?.email,
          extraData: {
            challengeName: challengeData?.title,
            challengeId: selectedChallenge,
          },
        });
        
        toast.info("Code reset to template");
      }
    }
  };

  return (
    <div className="w-full">
      <Card className="border bg-slate-900 text-slate-100 shadow-md overflow-hidden transition-all duration-300">
        <CardHeader className="bg-slate-800 border-b border-slate-700 pb-4">
          <div className="flex items-center gap-2 mb-1">
            <Code className="h-5 w-5 text-purple-400" />
            <CardTitle className="text-slate-100 text-xl">Interactive ML Coding Playground</CardTitle>
          </div>
          <CardDescription className="text-slate-400">
            Learn machine learning concepts through interactive coding examples
          </CardDescription>
          
          {/* Section Tabs */}
          <Tabs value={selectedSection} onValueChange={(value) => {
            const newSection = value as "theory" | "practice";
            trackPlaygroundInteraction("section_changed", {
              userId: user?.id,
              userEmail: user?.email,
              extraData: {
                fromSection: selectedSection,
                toSection: newSection,
              },
            });
            setSelectedSection(newSection);
            setSelectedChallenge(null);
          }} className="mt-4">
            <TabsList className="bg-slate-700/50 w-full">
              <TabsTrigger value="theory" className="flex-1 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Easy ML Theory
              </TabsTrigger>
              <TabsTrigger value="practice" disabled className="flex-1 text-slate-500 cursor-not-allowed">
                Advanced ML Practice (Coming Soon)
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-12 min-h-[400px]">
          {/* Left Sidebar - Challenge Selection */}
          <div className="md:col-span-3 bg-slate-800 border-r border-slate-700 p-4 overflow-hidden">
            <h3 className="text-sm uppercase text-slate-400 font-medium mb-3 flex items-center gap-2">
              <BookOpen className="h-4 w-4" /> 
              {selectedSection === "theory" ? "Theory Challenges" : "Practice Challenges"}
            </h3>
            
            <div className="flex flex-col gap-3">
              {/* Language Selector */}
              <Tabs value={selectedCategory} className="mb-4" onValueChange={(value) => {
                const newLanguage = value as "python" | "javascript" | "tensorflow";
                trackPlaygroundInteraction("language_changed", {
                  userId: user?.id,
                  userEmail: user?.email,
                  extraData: {
                    fromLanguage: selectedCategory,
                    toLanguage: newLanguage,
                    section: selectedSection,
                  },
                });
                setSelectedCategory(newLanguage);
                setSelectedChallenge(null);
              }}>
                <TabsList className="mb-2 bg-slate-700/50 w-full">
                  <TabsTrigger value="python" className="text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white">Python</TabsTrigger>
                  <TabsTrigger value="tensorflow" className="text-xs data-[state=active]:bg-green-600 data-[state=active]:text-white">TensorFlow</TabsTrigger>
                </TabsList>
              </Tabs>
              
              {/* Dataset Selector - Only for Advanced Practice */}
              {selectedSection === "practice" && (
                <div className="mb-4">
                  <DatasetSelector onDatasetLoaded={(datasetCode) => {
                    setCode(datasetCode);
                    setOutput("$ Dataset loaded! Run the code to see the data info");
                  }} />
                </div>
              )}
            </div>
            
            <div className="space-y-2 overflow-auto max-h-[300px] pr-1 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800 mt-3">
              {filteredExamples.map((example) => {
                const isCompleted = completedChallenges.includes(example.id);
                
                return (
                  <div 
                    key={example.id}
                    className={`p-3 rounded-md cursor-pointer border transition-all duration-200 hover:scale-[1.02] ${
                      selectedChallenge === example.id 
                        ? 'bg-slate-700 border-purple-500 shadow-lg shadow-purple-500/20' 
                        : 'bg-slate-800 border-slate-700 hover:bg-slate-700/50'
                    }`}
                    onClick={() => {
                      setSelectedChallenge(example.id);
                      trackPlaygroundInteraction("challenge_selected", {
                        userId: user?.id,
                        userEmail: user?.email,
                        extraData: {
                          challengeName: example.title,
                          challengeId: example.id,
                          difficulty: example.difficulty,
                          language: example.language,
                          tags: example.tags,
                          isCompleted: completedChallenges.includes(example.id),
                        },
                      });
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="text-sm font-medium text-slate-200 flex items-center gap-1.5">
                        {isCompleted && (
                          <CheckCircle className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
                        )}
                        {example.title}
                      </h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        example.difficulty === "Beginner" ? "bg-blue-900/40 text-blue-300" : 
                        "bg-purple-900/40 text-purple-300"
                      }`}>
                        {example.difficulty}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2">{example.description}</p>
                    {/* Company and Tags display */}
                    {example.companies && (
                      <div className="mt-2">
                        <span className="text-[10px] bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded font-medium">
                          Asked at: {example.companies}
                        </span>
                      </div>
                    )}
                    {example.tags && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {example.tags.map((tag, index) => (
                          <span key={index} className="text-[10px] bg-slate-700/60 text-slate-300 px-1.5 py-0.5 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Status indicator */}
                    <div className="flex justify-between items-center mt-2 pt-1 border-t border-slate-700/50">
                      <span className="text-[10px] flex items-center gap-1">
                        {isCompleted ? (
                          <span className="text-green-400 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" /> Completed
                          </span>
                        ) : (
                          <span className="text-slate-400 flex items-center gap-1">
                            <Clock className="h-3 w-3" /> Unattempted
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
              
              {filteredExamples.length === 0 && (
                <div className="p-4 text-center text-slate-400 text-sm">
                  No challenges available for the selected filters
                </div>
              )}
            </div>
          </div>
          
          {/* Main Content Area - Code Editor and Output */}
          <div className="md:col-span-9 flex flex-col">
            {selectedChallenge ? (
              <div className="flex flex-col h-full">
                {/* Challenge Details */}
                <div className="p-4 border-b border-slate-700">
                  {playgroundExamples.find(ex => ex.id === selectedChallenge) && (
                    <>
                      <div className="flex items-center justify-between">
                        <h2 className="text-lg font-medium text-slate-100">
                          {playgroundExamples.find(ex => ex.id === selectedChallenge)?.title}
                        </h2>
                        {completedChallenges.includes(selectedChallenge) && (
                          <span className="bg-green-900/30 text-green-300 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" /> Completed
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-400 mt-1">
                        {playgroundExamples.find(ex => ex.id === selectedChallenge)?.description}
                      </p>
                      {playgroundExamples.find(ex => ex.id === selectedChallenge)?.companies && (
                        <p className="text-xs text-slate-500 mt-1 italic">
                          {playgroundExamples.find(ex => ex.id === selectedChallenge)?.companies}
                        </p>
                      )}
                    </>
                  )}
                </div>
                
                {/* Code Editor */}
                <div className="flex-1 p-4 bg-slate-950 border-b border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Code className="h-4 w-4 text-blue-400" /> Code Editor
                    </h3>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors"
                        onClick={handleSaveCode}
                      >
                        <Save className="mr-1 h-3 w-3" /> Save
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 transition-colors"
                        onClick={handleResetCode}
                      >
                        <RefreshCw className="mr-1 h-3 w-3" /> Reset
                      </Button>
                      <Button 
                        size="sm" 
                        className={`h-7 text-xs transition-all ${
                          isRunning 
                            ? "bg-indigo-600 hover:bg-indigo-700" 
                            : "bg-purple-600 hover:bg-purple-700 hover:scale-105"
                        }`}
                        onClick={handleRunCode}
                        disabled={isRunning}
                      >
                        {isRunning ? (
                          <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Running...</>
                        ) : (
                          <><Play className="mr-1 h-3 w-3" /> Run Tests</>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className={`h-[240px] bg-slate-900 border border-slate-700 rounded-md overflow-hidden transition-all duration-300 ${
                    showSuccessAnimation ? 'ring-2 ring-green-500' : ''
                  }`}>
                    <CodeEditor 
                      code={code}
                      onChange={setCode}
                    />
                  </div>
                </div>
                
                {/* Output/Results Panel */}
                <div className="p-4 bg-slate-900 space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2 mb-3">
                      <Terminal className="h-4 w-4 text-green-400" /> Output
                    </h3>
                    <div className={`h-[100px] bg-slate-950 border ${
                      hasError ? 'border-red-700' : 'border-slate-700'
                    } rounded-md p-3 font-mono text-xs ${
                      hasError ? 'text-red-400' : 'text-green-400'
                    } overflow-auto transition-colors duration-300`}>
                      {isRunning ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-3 w-3 animate-spin" /> 
                          <span>Running tests...</span>
                        </div>
                      ) : (
                        output.split('\n').map((line, index) => (
                          <p key={index} className="transition-opacity duration-100">{line}</p>
                        ))
                      )}
                    </div>
                  </div>

                  {testResults.length > 0 && (
                    <TestResultsPanel
                      results={testResults}
                      totalTests={testStats.total}
                      passedTests={testStats.passed}
                    />
                  )}

                  {!isPyodideReady && !isRunning && (
                    <div className="bg-blue-900/20 border border-blue-700/30 rounded-md p-3">
                      <p className="text-xs text-blue-300">
                        💡 First run will take ~10 seconds to load the Python environment. Subsequent runs will be instant!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full p-8 text-slate-500">
                {/* Empty state */}
              </div>
            )}
          </div>
        </div>
        
        <CardFooter className="bg-slate-800 border-t border-slate-700 p-4 flex justify-between items-center">
          <div className="flex items-center">
            <span className="text-xs text-slate-400">Python v3.9.0</span>
            {completedChallenges.length > 0 && (
              <span className="ml-4 text-xs bg-green-900/30 text-green-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> {completedChallenges.length} {completedChallenges.length === 1 ? 'Challenge' : 'Challenges'} Completed
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" asChild className="bg-slate-700 border-slate-600 hover:bg-slate-600 transition-colors">
            <a href="/docs/playground">
              View Documentation <ArrowRight className="ml-1 h-3 w-3" />
            </a>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default CodingPlayground;
